import { Step } from "react-joyride";
import { TOUR_TYPES, TourType } from "../store/tour-store";

// Desktop tour steps for the deals table
export const dealTableSteps: Step[] = [
  {
    target: "body",
    content:
      "Welcome to DiligenceGPT! Here's a quick guide to help you get started.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour="add-deal-button"]',
    content: "Click here to add a new deal to your pipeline.",
    placement: "bottom",
  },
  {
    target: ".table-fixed",
    content:
      "Your deals will appear in this table. You can filter and manage your pipeline to stay organized.",
    placement: "top",
  },
  {
    target: "body",
    content:
      "Click on 'Demo Deal' to view its detailed information. Let's do that now!",
    placement: "center",
  },
];

// Desktop tour for deal details view
export const dealDetailSteps: Step[] = [
  {
    target: '[data-tour="deal-header"]',
    content:
      "This is your deal overview. You can see key information about the company here.",
    placement: "bottom",
  },
  {
    target: '[data-tour="data-lake"]',
    content:
      "The Data Lake section contains all your deal information, including the Deal Wall and Data Room.",
    placement: "right",
  },
  {
    target: '[data-tour="reports"]',
    content:
      "Generate and view various reports about your deal, including Data Sufficiency and Deal Brief.",
    placement: "right",
  },
  {
    target: '[data-tour="diligencegpt-chat"]',
    content: "Use DiligenceGPT to get AI-powered insights about your deal.",
    placement: "left",
  },
];

// Mobile tour steps for the deals table
export const mobileDealTableSteps: Step[] = [
  {
    target: '[data-tour="mobile-container"]',
    content:
      "Welcome to DiligenceGPT! Here's a quick guide to help you get started on mobile.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour="add-deal-button"]',
    content: "Add new deals to your pipeline with this button.",
    placement: "bottom",
  },
  {
    target: ".status-header",
    content:
      "Your deals are organized by status. Tap the headers to expand or collapse each section.",
    placement: "bottom",
    floaterProps: {
      disableAnimation: true,
    },
    spotlightPadding: 8,
  },
  {
    target: ".demo-deal-row",
    content: "Tap 'Demo Deal' to view its details. Let's do that now!",
    spotlightClicks: true,
    floaterProps: {
      disableAnimation: true,
    },
    spotlightPadding: 4,
  },
];

// Mobile tour for deal details view
export const mobileDealDetailSteps: Step[] = [
  {
    target: '[data-tour="mobile-menu-button"]',
    content:
      "Access the deal menu here to navigate through different sections.",
    placement: "bottom",
  },
  {
    target: '[data-tour="mobile-chat-button"]',
    content:
      "Open DiligenceGPT chat to get AI-powered insights about your deal.",
    placement: "top",
  },
  {
    target: '[data-tour="deal-header"]',
    content: "View and manage key information about your deal here.",
    placement: "bottom",
  },
];

// Sidebar tour steps
export const sidebarSteps: Step[] = [
  {
    target: "body",
    content:
      "Welcome to DiligenceGPT! Let's explore the key navigation elements.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour="dashboard"]',
    content:
      "This is your Dashboard - get a high-level overview of your workspace.",
    placement: "right",
  },
  {
    target: '[data-tour="deals"]',
    content: "Access your Deals pipeline to manage and track deals.",
    placement: "right",
  },
  {
    target: '[data-tour="portco"]',
    content: "View your Portfolio Companies and their performance.",
    placement: "right",
  },
  {
    target: '[data-tour="team"]',
    content: "Collaborate with your Team members and manage permissions.",
    placement: "right",
  },
  {
    target: '[data-tour="deal-radar"]',
    content: "Tap here to explore opportunities in the Deal Radar.",
    placement: "right",
  },
  {
    target: '[data-tour="workspace-settings"]',
    content: "Configure your workspace preferences and investment criteria.",
    placement: "top",
  },
  {
    target: '[data-tour="support"]',
    content: "Need help? Access our Support documentation and resources.",
    placement: "top",
  },
  {
    target: '[data-tour="workspace-switcher"]',
    content:
      "Switch between different workspaces or add new ones to manage multiple opportunities.",
    placement: "top",
  },
];

// Common styles for all tours
export const tourStyles = {
  options: {
    primaryColor: "#7C3AED",
    zIndex: 10000,
  },
  tooltip: {
    backgroundColor: "white",
    textColor: "black",
  },
  spotlight: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  buttonClose: {
    display: "none",
  },
};

// Sidebar tour specific styles
export const sidebarTourStyles = {
  options: {
    primaryColor: "#C49733",
    zIndex: 1000,
  },
  tooltip: {
    backgroundColor: "white",
    textColor: "black",
    padding: 16,
    borderRadius: 8,
  },
  spotlight: {
    backgroundColor: "rgba(196, 151, 51, 0.4)",
  },
  tooltipContainer: {
    textAlign: "left" as const,
  },
  buttonNext: {
    backgroundColor: "#C49733",
    fontSize: 14,
  },
  buttonBack: {
    color: "#C49733",
    fontSize: 14,
  },
  buttonSkip: {
    color: "#6B7280",
    fontSize: 14,
  },
  buttonClose: {
    display: "none",
  },
};

// Tour configuration mapping
type TourConfig = {
  steps: Step[];
  styles: any;
};

export const TOUR_CONFIGS: Record<string, TourConfig> = {
  [TOUR_TYPES.DEALS_TABLE]: {
    steps: dealTableSteps,
    styles: tourStyles,
  },
  [TOUR_TYPES.DEAL_DETAIL]: {
    steps: dealDetailSteps,
    styles: tourStyles,
  },
  [TOUR_TYPES.MOBILE_DEALS_TABLE]: {
    steps: mobileDealTableSteps,
    styles: tourStyles,
  },
  [TOUR_TYPES.MOBILE_DEAL_DETAIL]: {
    steps: mobileDealDetailSteps,
    styles: tourStyles,
  },
  [TOUR_TYPES.SIDEBAR]: {
    steps: sidebarSteps,
    styles: sidebarTourStyles,
  },
};

// Helper function to get tour configuration
export const getTourConfig = (tourType: TourType) => {
  return TOUR_CONFIGS[tourType] || null;
};

// Validation utility to check if all tour targets exist in the DOM
export const validateTourTargets = (
  tourType: TourType
): { valid: boolean; missingTargets: string[] } => {
  const config = getTourConfig(tourType);
  if (!config) {
    return {
      valid: false,
      missingTargets: [`Tour config not found for: ${tourType}`],
    };
  }

  const missingTargets: string[] = [];

  config.steps.forEach((step, index) => {
    if (
      step.target &&
      typeof step.target === "string" &&
      step.target !== "body"
    ) {
      const elements = document.querySelectorAll(step.target);
      if (elements.length === 0) {
        missingTargets.push(`Step ${index + 1}: ${step.target}`);
      }
    }
  });

  return {
    valid: missingTargets.length === 0,
    missingTargets,
  };
};

// Enhanced tour configuration with validation
export const getValidatedTourConfig = (tourType: TourType) => {
  const config = getTourConfig(tourType);
  if (!config) return null;

  // Validate targets before returning config
  const validation = validateTourTargets(tourType);

  return {
    ...config,
    validation,
  };
};
